
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { Waybill, PeriodLock, WaybillBlank, StockLocation, StockMovement, FuelCard } from '../../types';

// Описываем структуру БД для TypeScript
export interface AppDB extends DBSchema {
  waybills: {
    key: string;
    value: Waybill;
    indexes: {
      'by-date': string;
      'by-vehicle': string;
      'by-status': string;
    };
  };
  periodLocks: {
    key: string;
    value: PeriodLock;
    indexes: {
      'by-period': string;
    };
  };
  blanks: {
    key: string;
    value: WaybillBlank;
    indexes: {
      'by-series': string;
      'by-status': string;
    };
  };
  stock_locations_v1: {
    key: string;
    value: StockLocation;
    indexes: {
      'by-type': string;
      'by-vehicle': string;
      'by-fuelCard': string;
    };
  };
  stock_movements_v1: {
    key: string;
    value: StockMovement;
    indexes: {
      'by-externalRef': string;
      'by-from': string;
      'by-to': string;
      'by-document': [string, string];
      'by-date': string;
    };
  };
  fuel_cards_v1: {
    key: string;
    value: FuelCard;
    indexes: {
      'by-cardNumber': string;
      'by-driver': string;
      'by-vehicle': string;
    };
  };
}

// Новое имя БД для избежания конфликта с localforage
const DB_NAME = 'WaybillAppDB';
// Сброс версии — начинаем с чистой базы (v2 для Fuel Ledger)
const DB_VERSION = 2;

let dbPromise: Promise<IDBPDatabase<AppDB>> | null = null;

export const getDb = () => {
  if (!dbPromise) {
    dbPromise = (async () => {
      // console.log(`[DB] Opening ${DB_NAME} v${DB_VERSION}...`);

      return openDB<AppDB>(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion, newVersion, transaction) {
          // console.log(`[DB] Upgrading from v${oldVersion} to v${newVersion}`);

          // 1. Waybills store
          if (!db.objectStoreNames.contains('waybills')) {
            const waybillStore = db.createObjectStore('waybills', { keyPath: 'id' });
            waybillStore.createIndex('by-date', 'date');
            waybillStore.createIndex('by-vehicle', 'vehicleId');
            waybillStore.createIndex('by-status', 'status');
          }

          // 2. PeriodLocks store
          if (!db.objectStoreNames.contains('periodLocks')) {
            const lockStore = db.createObjectStore('periodLocks', { keyPath: 'id' });
            lockStore.createIndex('by-period', 'period');
          }

          // 3. Blanks store
          if (!db.objectStoreNames.contains('blanks')) {
            const blankStore = db.createObjectStore('blanks', { keyPath: 'id' });
            blankStore.createIndex('by-series', 'series');
            blankStore.createIndex('by-status', 'status');
          }

          // 4. Stock Locations (v2)
          if (!db.objectStoreNames.contains('stock_locations_v1')) {
            const store = db.createObjectStore('stock_locations_v1', { keyPath: 'id' });
            store.createIndex('by-type', 'type');
            store.createIndex('by-vehicle', 'vehicleId');
            store.createIndex('by-fuelCard', 'fuelCardId');
          }

          // 5. Stock Movements (v2)
          if (!db.objectStoreNames.contains('stock_movements_v1')) {
            const store = db.createObjectStore('stock_movements_v1', { keyPath: 'id' });
            store.createIndex('by-externalRef', 'externalRef', { unique: true });
            store.createIndex('by-from', 'fromStockLocationId');
            store.createIndex('by-to', 'toStockLocationId');
            store.createIndex('by-document', ['documentType', 'documentId']);
            store.createIndex('by-date', 'occurredAt');
          }

          // 6. Fuel Cards (v2)
          if (!db.objectStoreNames.contains('fuel_cards_v1')) {
            const store = db.createObjectStore('fuel_cards_v1', { keyPath: 'id' });
            store.createIndex('by-cardNumber', 'cardNumber', { unique: true });
            store.createIndex('by-driver', 'driverId');
            store.createIndex('by-vehicle', 'vehicleId');
          }
        },
        blocked(currentVersion, blockedVersion, event) {
          console.warn(`[DB] Database blocked: current=${currentVersion}, blocked=${blockedVersion}`);
        },
        blocking(currentVersion, blockedVersion, event) {
          console.warn(`[DB] Database blocking update: current=${currentVersion}, blocked=${blockedVersion}. Closing connection.`);
          const db = (event.target as any).result;
          if (db) {
            db.close();
          }
        },
        terminated() {
          console.error('[DB] Database terminated unexpectedly');
          dbPromise = null;
        },
      });
    })();
  }
  return dbPromise;
};
